// ════════════════════════════════════════════════════════════
//  climb/climbStore.js — where the Climb is saved
// ════════════════════════════════════════════════════════════
//  FIREBASE (2026-10-21, caP'n: "yes do that first"): a signed-in player's Climb lives at
//  games/shatterproof/climb/{uid} = { rec, run, at } — so a run follows you between screens,
//  two windows see ONE run, and Lyraei's climb lives in her own record. No rules paste: the
//  `games/$gameId` umbrella already allows it (ie-firebase-rules).
//
//  · ONE LISTENER per player, attached the first time anything asks. A tiny node: no drain.
//  · The SERVER WINS. This browser keeps a mirror (localStorage, marked m:1) so the page draws at
//    once. Only an UNMARKED (pre-Firebase) save is ever uploaded — once — so a Climb wiped on the
//    server stays wiped (caP'n's delete-forever rule).
//  · rec and run are written TOGETHER, so a result never lands half.
//  · A BOUT IS CLAIMED before it's fought, and SETTLED after, each in a server TRANSACTION
//    (review 2026-10-21: two windows could fight one bout, skip the next and pay a King's Krest
//    twice). The claim marks the run with this window (liveTab) and the server's time (liveAt);
//    the window fighting it beats that every BEAT_MS. Only that window — or anyone, once the mark
//    is LIVE_STALE_MS old (a closed window) — may count a walked-out bout as lost.
//  · Can't reach the server: after READY_WAIT_MS the Climb shows what it has, VIEW-ONLY until the
//    server answers (an old copy must never overwrite newer progress from another device).
//  · Not signed in: this browser only ("guest"). A Pier guest pass (signed in, can't write): this
//    browser only, under "~uid" — the same browser key the scaffold used, so nothing looks wiped.
// ════════════════════════════════════════════════════════════
import { normalRecord } from "./climb.js";
import { BASE, serverNow, clockKnown } from "../online/ie.js";
import { fbNet } from "../online/net.js";

export const READY_WAIT_MS = 6000;      // ⟵ DIAL: how long to wait for the server before showing the mirror (view-only)
export const BEAT_MS = 30 * 1000;       // ⟵ DIAL: how often the window fighting a bout says "still here"
export const LIVE_STALE_MS = 5 * 60 * 1000; // ⟵ DIAL: a "being fought" mark older than this MAY be a closed window (a person decides — review 2026-10-21: a phone app in the background freezes its beat)
export const climbPath = (uid) => `${BASE}/climb/${uid}`;

let NET = fbNet;
let CLOCK = () => serverNow();
let CLOCK_OK = () => clockKnown();
/** Tests only: a fake network and clock (tools/climbstore-check.mjs). Clears everything. */
export function setClimbNet(net, clock = null) { NET = net; if (clock) { CLOCK = clock; CLOCK_OK = () => true; } for (const s of slots.values()) s.off && s.off(); slots.clear(); }

export let TAB_ID = (() => {
  try {
    let t = sessionStorage.getItem("shatterproof.tab");
    if (!t) { t = Math.random().toString(36).slice(2, 10); sessionStorage.setItem("shatterproof.tab", t); }
    return t;
  } catch { return Math.random().toString(36).slice(2, 10); }
})();
/** Tests only: pretend to be another window. */
export const setTabId = (t) => { TAB_ID = t; };

const remoteOf = (uid) => !!uid && uid[0] !== "~";            // "~uid" = a guest pass: never the server
// ── the browser mirror ──
const KEY = (uid) => `shatterproof.climb.${uid ? uid.replace(/^~/, "") : "guest"}`;
function readLocal(uid) {
  try { const v = JSON.parse(localStorage.getItem(KEY(uid)) || "null"); return v && typeof v === "object" ? v : null; } catch { return null; }
}
function writeLocal(uid, v) { try { localStorage.setItem(KEY(uid), JSON.stringify(remoteOf(uid) ? { ...v, m: 1 } : v)); } catch { /* can't save here: fine */ } }

// ── one slot per player: { v: {rec, run}, ready, offline, busy, fans, off } ──
const slots = new Map();
const shape = (v) => ({ rec: normalRecord(v && v.rec), run: (v && v.run) || null });
function tell(s) { for (const f of [...s.fans]) { try { f(); } catch { /* a screen's problem */ } } }

function slotOf(uid) {
  const k = uid || "guest";
  let s = slots.get(k);
  if (s) return s;
  const local = readLocal(uid);
  s = { v: shape(local), ready: !remoteOf(uid), offline: false, busy: 0, fans: new Set(), off: null };
  slots.set(k, s);
  if (remoteOf(uid)) {
    let first = true;
    const wait = setTimeout(() => { if (!s.ready) { s.ready = true; s.offline = true; tell(s); } }, READY_WAIT_MS);
    s.off = NET.onValue(climbPath(uid), (remote) => {
      if (first) {
        first = false; clearTimeout(wait);
        if (remote == null && local && !local.m && (local.rec || local.run)) {   // an old (pre-Firebase) save goes up once
          NET.update(climbPath(uid), { rec: s.v.rec, run: s.v.run, at: NET.TS() }).catch(() => {});
          s.ready = true; s.offline = false; tell(s);
          return;
        }
      }
      s.v = shape(remote);                                                       // the server wins (null = nothing / wiped)
      writeLocal(uid, s.v);
      s.ready = true; s.offline = false; tell(s);
    });
  }
  return s;
}

/** What's saved: { rec, run, ready, offline, busy }.
 *  canAct = ready && !offline: the only time Start / Fight / Give up may write. busy = a settle is on its way. */
export const loadClimb = (uid) => { const s = slotOf(uid); return { ...s.v, ready: s.ready, offline: s.offline, busy: s.busy > 0, canAct: s.ready && !s.offline }; };
/** Calls fn whenever this player's Climb changes (here, another window, another device). Returns the unsubscribe. */
export function watchClimb(uid, fn) { const s = slotOf(uid); s.fans.add(fn); return () => s.fans.delete(fn); }

/** Save rec and run TOGETHER (run: null ends it), no questions asked. Every in-game step uses the guarded verbs below. */
export function saveClimb(uid, { rec, run }) {
  const s = slotOf(uid);
  s.v = { rec: normalRecord(rec), run: run || null };
  writeLocal(uid, s.v);
  if (remoteOf(uid)) NET.update(climbPath(uid), { rec: s.v.rec, run: s.v.run, at: NET.TS() }).catch(() => { /* Firebase retries on its own */ });
  tell(s);
}

/** fn({rec, run}) → the next {rec, run}, or undefined to refuse. On the server it's a TRANSACTION (fn may run more
 *  than once — keep it pure). Resolves true when it landed. */
async function change(uid, fn) {
  const s = slotOf(uid);
  if (!remoteOf(uid)) {
    const next = fn(s.v);
    if (!next) return false;
    s.v = shape(next); writeLocal(uid, s.v); tell(s);
    return true;
  }
  s.busy++; tell(s);
  try {
    const r = await NET.txn(climbPath(uid), (cur) => {
      const next = fn(shape(cur));                              // nothing there yet = a blank record, no run
      return next ? { rec: normalRecord(next.rec), run: next.run || null, at: (cur && cur.at) || null } : undefined;
    });
    return !!r.committed;
  } catch { return false; } finally { s.busy--; tell(s); }
}

/** Mark a run as being fought by THIS window, at the server's time. */
export const liveRun = (run, now = CLOCK()) => ({ ...run, live: true, liveTab: TAB_ID, liveAt: now });
/** Who's fighting it: "none" · "here" (this window — it walked out if it isn't fighting now) · "elsewhere" · "stale" (a closed window). */
export function liveState(run, now = CLOCK()) {
  if (!run || !run.live) return "none";
  if (run.liveTab == null || run.liveTab === TAB_ID) return "here";       // no window mark = a scaffold-era save, made here
  return CLOCK_OK() && now - (run.liveAt || 0) > LIVE_STALE_MS ? "stale" : "elsewhere";   // never on a guessed clock
}
const sameBout = (a, b) => !!a && !!b && a.startedAt === b.startedAt && a.bout === b.bout;

/** CLAIM the bout `run` is on, for this window. Refused if another window holds it, or the run moved on. */
export const claimBout = (uid, run) => change(uid, (cur) => {
  if (!sameBout(cur.run, run) || cur.run.live) return undefined;   // a bout already fought (or being fought) is never re-claimed
  return { rec: cur.rec, run: liveRun(cur.run) };
});

/** SETTLE the bout this window claimed: fn(run, rec) → finishBout's { run, rec, ... }.
 *  Only if it's still the same bout and still this window's. Resolves fn's last answer, or null when refused. */
export async function settleBout(uid, mine, fn) {
  let out = null;
  const ok = await change(uid, (cur) => {
    const r = cur.run;
    if (!sameBout(r, mine) || !r.live || (r.liveTab != null && r.liveTab !== TAB_ID)) { out = null; return undefined; }
    out = fn(r, cur.rec);
    return { rec: out.rec, run: out.run };
  });
  return ok ? out : null;
}

/** "Still here" for the bout this window is fighting (call every BEAT_MS). */
export const beatBout = (uid, mine) => change(uid, (cur) => {
  if (!sameBout(cur.run, mine) || !cur.run.live || cur.run.liveTab !== TAB_ID) return undefined;
  return { rec: cur.rec, run: { ...cur.run, liveAt: CLOCK() } };
});

/** COUNT a walked-out bout as lost: fn(run, rec) → abandon's { run, rec }. "here" happens on its own (this window walked
 *  out); "stale" ONLY when a person says so ("That window's gone"), never automatically. */
export const endWalkedOut = (uid, run, fn) => change(uid, (cur) => {
  if (!sameBout(cur.run, run)) return undefined;
  const st = liveState(cur.run);
  if (st !== "here" && st !== "stale") return undefined;
  const out = fn(cur.run, cur.rec);
  return { rec: out.rec, run: out.run };
});

/** START a run — only if none is on (another window may have just started one). run(rec) → the new run. */
export const beginRun = (uid, makeRun) => change(uid, (cur) => {
  if (cur.run) return undefined;
  return { rec: { ...cur.rec, runs: (cur.rec.runs || 0) + 1 }, run: makeRun(cur.rec) };
});

/** GIVE UP between bouts (counts as losing the next one): fn(run, rec) → abandon's { run, rec }. Not while another window fights it. */
export const giveUpRun = (uid, run, fn) => change(uid, (cur) => {
  if (!cur.run || cur.run.startedAt !== run.startedAt || liveState(cur.run) === "elsewhere") return undefined;
  const out = fn(cur.run, cur.rec);
  return { rec: out.rec, run: out.run };
});

/** The key the Climb saves under: a real sign-in → the server; a Pier guest pass → "~uid" (this browser); else guest. */
export const climbUid = (on) => (on && on.authUid ? (on.writable ? on.authUid : `~${on.authUid}`) : null);
